import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, Sparkles } from 'lucide-react';
import { botsApi } from '../../../api/bots.api';

const BotSettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [bots, setBots] = useState([]);
  const [botId, setBotId] = useState('');
  const [loading, setLoading] = useState(true);
  const [flowLoading, setFlowLoading] = useState(false);
  const [error, setError] = useState('');
  const [blocks, setBlocks] = useState([]);

  const loadBots = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await botsApi.getAll();
      const rows = response?.data?.data || response?.data || [];
      setBots(Array.isArray(rows) ? rows : []);
      const qsBotId = searchParams.get('botId');
      const fallback = rows?.[0]?.bid;
      setBotId(qsBotId || fallback || '');
    } catch (err) {
      setError('Failed to load bots.');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const loadFlow = useCallback(async (id) => {
    if (!id) return;
    setFlowLoading(true);
    setError('');
    try {
      const response = await botsApi.getWaFlow(id);
      const payload = response?.data?.data || response?.data || {};
      const rows = payload?.blocks || [];
      setBlocks(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError('Failed to load flow details.');
      setBlocks([]);
    } finally {
      setFlowLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBots();
  }, [loadBots]);

  useEffect(() => {
    if (botId) {
      loadFlow(botId);
    }
  }, [botId, loadFlow]);

  const botName = useMemo(() => {
    const match = bots.find((bot) => String(bot.bid) === String(botId));
    return match?.b_name || 'Bot';
  }, [bots, botId]);

  const handleCleanup = async () => {
    if (!botId) return;
    setError('');
    try {
      await botsApi.cleanupWaConnections(botId);
      await loadFlow(botId);
    } catch (err) {
      setError('Cleanup failed. Please try again.');
    }
  };

  return (
    <Box sx={{ p: 3, fontFamily: '"Space Grotesk", "Segoe UI", sans-serif' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={18} />}
          onClick={() => navigate('/bots')}
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight={700}>
          Bot Settings
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>Select Bot</InputLabel>
                  <Select
                    value={botId}
                    label="Select Bot"
                    onChange={(e) => setBotId(e.target.value)}
                  >
                    {bots.map((bot) => (
                      <MenuItem key={bot.bid} value={bot.bid}>
                        {bot.b_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    startIcon={<RefreshCcw size={16} />}
                    onClick={() => loadFlow(botId)}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Sparkles size={16} />}
                    onClick={handleCleanup}
                  >
                    Cleanup Connections
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack spacing={1.5} mb={2}>
                <Typography variant="h6">{botName} Flow Summary</Typography>
                <Typography color="text.secondary">
                  Keep an eye on block health and connected paths.
                </Typography>
              </Stack>
              {flowLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : blocks.length === 0 ? (
                <Typography color="text.secondary">No blocks created yet.</Typography>
              ) : (
                <Stack spacing={2}>
                  {blocks.map((block) => (
                    <Box
                      key={block.id}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: block.is_start ? 'rgba(34,197,94,0.08)' : 'transparent',
                      }}
                    >
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {block.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {block.type.replace('_', ' ')}
                          </Typography>
                          {block.message?.body && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {block.message.body.slice(0, 120)}
                            </Typography>
                          )}
                        </Box>
                        <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                          {block.is_start && (
                            <Typography variant="caption" color="success.main">
                              Start Block
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Connections: {block.connections?.length || 0}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
};

export default BotSettingsPage;
